package com.evoapps.evoshift.domain.model

import com.evoapps.evoshift.domain.market.Market
import kotlin.math.max

/**
 * SH-113: what a manager set for one person's annual leave at one workplace in one year
 * (workplaces/{wpId}/leave_allowances/{userId}_{year}). The person reads their own, the
 * workplace's managers read and write everyone's (firestore.rules).
 */
data class LeaveAllowance(
    val userId: String,
    val workplaceId: String,
    val year: Int,
    /** The year's entitlement in days as the manager set it; null: worked out from the contract. */
    val entitlementDays: Int? = null,
    /** Days left from the year before. */
    val carryOverDays: Int = 0,
    val updatedAt: String? = null,
    val updatedBy: String? = null
) {
    companion object {
        /** Most days a manager can enter for the entitlement or the carry-over (the rules allow the same). */
        const val MAX_DAYS = 60

        fun docId(userId: String, year: Int) = "${userId}_$year"
    }
}

/** A manager entered days outside 0..[LeaveAllowance.MAX_DAYS]. */
class InvalidLeaveAllowanceException : IllegalArgumentException("Leave days must be between 0 and ${LeaveAllowance.MAX_DAYS}.")

/**
 * SH-113: the facts one person's leave balance is worked out from, apart from the absences: the
 * entitlement the contract gives ([statutoryDays], pro rata) and what a manager set.
 */
data class LeaveEntitlement(
    val userId: String,
    val workplaceId: String,
    val year: Int,
    val market: Market,
    /**
     * The year's entitlement from the contract: full-time days × FTE × the months engaged this year
     * / 12, a started day rounded up. Null: unknown to the reader (no access to the pay terms) or
     * the contract carries no statutory leave.
     */
    val statutoryDays: Int?,
    /** The days the market allows on demand (PL: 4). */
    val onDemandLimit: Int,
    val allowance: LeaveAllowance? = null
)

/**
 * SH-113: one person's annual leave at one workplace in one year, in working days (weekends and
 * holidays do not count). Leave on demand is part of the annual leave, counted also on its own.
 */
data class LeaveBalance(
    val userId: String,
    val workplaceId: String,
    val year: Int,
    val market: Market,
    /** The entitlement from the contract (null: unknown or none), shown next to a manager's change. */
    val statutoryDays: Int?,
    /** The year's entitlement in force: the manager's, else the contract's, else 0. */
    val entitlementDays: Int,
    /** Whether a manager set [entitlementDays] instead of the contract. */
    val entitlementSetByManager: Boolean,
    val carryOverDays: Int,
    /** Approved annual leave and leave on demand this year. */
    val usedDays: Int,
    /** Requested and not decided yet. */
    val pendingDays: Int,
    /** Of [usedDays], the approved days on demand. */
    val onDemandUsedDays: Int,
    /** Of [pendingDays], the requested days on demand. */
    val onDemandPendingDays: Int,
    val onDemandLimit: Int
) {
    /** The entitlement and the carry-over. */
    val totalDays: Int get() = entitlementDays + carryOverDays

    /** Not taken yet (may be negative when more was approved than there was). */
    val remainingDays: Int get() = totalDays - usedDays

    /** Still free to ask for: the remaining days less the requests waiting for a decision. */
    val availableDays: Int get() = remainingDays - pendingDays

    /** Days on demand still free to ask for (never more than the annual leave left). */
    val onDemandAvailableDays: Int get() = max(0, minOf(onDemandLimit - onDemandUsedDays - onDemandPendingDays, availableDays))

    /** Whether the entitlement is known to the reader (a contract with leave, or a manager's figure). */
    val isKnown: Boolean get() = statutoryDays != null || entitlementSetByManager

    /** Whether there is anything to show: an entitlement, a carry-over or leave taken. */
    val hasAnything: Boolean get() = totalDays > 0 || usedDays > 0 || pendingDays > 0

    /**
     * What a new request of [days] working days ([onDemand]: on demand) leaves: within the balance,
     * over it, or over the days on demand.
     */
    fun check(days: Int, onDemand: Boolean): LeaveRequestCheck {
        val exceedsBalance = days > availableDays
        val exceedsOnDemand = onDemand && days > max(0, onDemandLimit - onDemandUsedDays - onDemandPendingDays)
        return LeaveRequestCheck(
            days = days,
            leftAfter = availableDays - days,
            exceedsBalance = exceedsBalance,
            exceedsOnDemand = exceedsOnDemand
        )
    }
}

/** SH-113: a leave request held against the balance. */
data class LeaveRequestCheck(
    /** Working days the request takes this year. */
    val days: Int,
    /** Days left once it is approved (negative: over the balance). */
    val leftAfter: Int,
    val exceedsBalance: Boolean,
    val exceedsOnDemand: Boolean
) {
    val exceeds: Boolean get() = exceedsBalance || exceedsOnDemand
}

/** SH-113: one row of the managers' leave balances. */
data class TeamLeaveBalance(
    val userId: String,
    val displayName: String,
    val contract: SystemContractType?,
    val entitlement: LeaveEntitlement,
    val balance: LeaveBalance
)
